import React, { useEffect } from "react"
import { useCookies } from "react-cookie"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useHistory, useLocation } from "react-router-dom"

import { useCompany } from "Shared/hooks/company.hooks"
import { useIsFreeVersion } from "Shared/hooks/featureFlags.hooks"
import { useLoggedInEmployeeProfile, useLoggedInUserProfile } from "Shared/hooks/user.hooks"
import { NotificationService, UtilityService } from "Shared/services"
import { NotificationTypeEnum } from "Shared/services/notification.service"
import { fetchDashboardInboxInvoicesCount } from "Shared/store/actions/inboxInvoice/inboxInvoiceActions"
import { KYBData } from "Shared/store/reducers/corporateReducer"
import { createCompanyIdCookieName } from "Shared/utils/cookie.utils"
import { isDev } from "Shared/utils/helpers"
import useStateIfMounted from "Shared/utils/hooks/useStateIfMounted"

import { useFeatureFlags } from "Shared/hooks/featureFlags.hooks"
import { ThunkDispatchResult } from "."
import { fetchAllActivities } from "./actions/activity/activityActions"
import { logout } from "./actions/auth/authActions"
import { fetchAllBankConnections } from "./actions/bank/bankActions"
import { fetchAllCompanies } from "./actions/company/companyActions"
import { fetchKYBProgress, setKYBData } from "./actions/corporate/corporateActions"
import { fetchAllCostCenters } from "./actions/costCenter/costCenterActions"
import { fetchAllCostCenters2 } from "./actions/costCenter2/costCenter2Actions"
import { fetchAllCsvTemplates } from "./actions/csvTemplates/csvTemplateActions"
import { fetchCurrencyExchange } from "./actions/currency/currencyActions"
import { fetchAllEmployees } from "./actions/employee/employeeActions"
import { fetchDashboardExpensesCounts } from "./actions/expense/expenseActions"
import { fetchAllExpenseAccounts } from "./actions/expenseAccount/expenseAccountActions"
import { fetchAllExpenseTags } from "./actions/expenseTag/expenseTagActions"
import { resetFetchFlag, setHasLoaded, setLoading } from "./actions/fetcher/fetcherActions"
import { fetchAllForecastRules, fetchAllScenarios } from "./actions/liquidity/liquidityActions"
import { fetchPaymentSettings } from "./actions/payment/paymentActions"
import { fetchPerDiemDestinations } from "./actions/perDiemDestination/perDiemDestinationActions"
import { initialFetchTables } from "./actions/tables/tableActions"
import { fetchAllTaxes } from "./actions/tax/taxActions"
import { fetchAllTeams } from "./actions/team/teamActions"
import { fetchAllTransactionCategories } from "./actions/transactionCategory/transactionCategoryAction"
import { fetchAllVendors } from "./actions/vendor/vendorActions"
import { fetchAllWorkflows } from "./actions/workflow/workflowActions"
import { RootState } from "./rootState.interface"
import { WEBAPP_VERSION } from "Shared/config/consts"

const DataFetcher = React.memo(() => {
    const dispatch: ThunkDispatchResult = useDispatch()
    const { t } = useTranslation()
    const history = useHistory()
    const location = useLocation()
    const [cookies, setCookie, removeCookie] = useCookies()

    const selectedCompany = useCompany()

    const [isFetching, setIsFetching] = useStateIfMounted(false)

    const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated)
    const user = useLoggedInEmployeeProfile()
    const profile = useLoggedInUserProfile()
    const companyIdCookieName = createCompanyIdCookieName(profile)
    const apiVersion = useSelector((state: RootState) => state.fetcher.apiVersion)
    const isFreeVersion = useIsFreeVersion()
    const featureFlags = useFeatureFlags()

    const fetchCompanies = async () => await dispatch(fetchAllCompanies())

    useEffect(() => {
        let enableFetching = true

        if (location.pathname.indexOf("login") > -1) {
            enableFetching = false
        }

        // * if Authenticated but companies are not fetched or companyId do not exist in cookies we should fetch companies
        if (isAuthenticated && !selectedCompany._id && !cookies[companyIdCookieName]) {
            enableFetching = false
            fetchCompanies()
        }

        // FA-351 Cookie migration code can be deleted after deployment
        if (cookies.companyId) {
            removeCookie("companyId", { secure: true, sameSite: "lax", path: "/", ...(!isDev && { domain: ".finway.de" }) })
            setCookie(companyIdCookieName, selectedCompany._id, { secure: !isDev, sameSite: "lax", path: "/", ...(!isDev && { domain: ".finway.de" }) })
        }

        // * If the selected companyId is different from the cookie companyId then update cookies
        if (cookies[companyIdCookieName] !== selectedCompany._id) {
            setCookie(companyIdCookieName, selectedCompany._id, { secure: !isDev, sameSite: "lax", path: "/", ...(!isDev && { domain: ".finway.de" }) })
            enableFetching = false
        }

        selectedCompany?.shouldRefetch && enableFetching && isAuthenticated && fetchData()

        return () => {
            enableFetching = false
        }
    }, [isAuthenticated, apiVersion, selectedCompany, cookies[companyIdCookieName]])

    useEffect(() => {
        if (user.activeCompanyProfile?.isAdmin) {
            fetchAdminData()
        } else if (user.activeCompanyProfile?.isAdmin !== undefined && !user.activeCompanyProfile.isAdmin && isFreeVersion) {
            // This should only reached when the paidVersion flag is false and the user is not admin.
            // To resolve this, the server needs to run migrations so permissions for user can be re-assigned because free version will change everyone as admin.
            NotificationService.send(NotificationTypeEnum.ERROR, t("error:loader.title"), t("error:loader.message"))
            history.push("/error")
            dispatch(logout())
        }
    }, [user.activeCompanyProfile?.isAdmin, isAuthenticated, apiVersion, selectedCompany, cookies[companyIdCookieName]])

    const fetchAdminData = async () =>
        await Promise.allSettled([
            dispatch(fetchAllBankConnections()),
            dispatch(fetchAllScenarios()),
            dispatch(fetchAllForecastRules()),
            dispatch(fetchAllTransactionCategories()),
            { ...(featureFlags?.stripeEnabled && dispatch(fetchPaymentSettings())) },
        ])

    const fetchData = async () => {
        await dispatch(setLoading())
        await dispatch(setKYBData({} as KYBData))

        if (isAuthenticated && !isFetching) {
            setIsFetching(true)
            try {
                const remoteApiVersion = await UtilityService.getApiVersion()
                console.log(`===== API version ${remoteApiVersion} =====`)
                if (remoteApiVersion !== WEBAPP_VERSION) {
                    NotificationService.send(NotificationTypeEnum.WARNING, t("error:outdated_version.title"), t("error:outdated_version.message"), 0)
                }

                // TODO: disabled so that it doesn't get logged out for migrations only.
                // TODO: enable again when we release a new big version that changes the redux store.
                // if (apiVersion < remoteApiVersion) {
                //     notification.open({
                //         type: "warning",
                //         message: t("notification:new_api_version.title"),
                //         description: t("notification:new_api_version.message"),
                //         duration: 0,
                //     })

                //     await logoutRedirect()
                //     setIsFetching(false)
                //     return
                // }

                try {
                    await Promise.allSettled([
                        {
                            ...(!isFreeVersion && [
                                dispatch(fetchCurrencyExchange()),
                                dispatch(fetchAllVendors()),
                                dispatch(fetchAllCostCenters()),
                                dispatch(fetchAllCostCenters2()),
                                dispatch(fetchAllActivities()),
                                dispatch(fetchDashboardExpensesCounts()),
                                dispatch(fetchDashboardInboxInvoicesCount()),
                                dispatch(fetchKYBProgress()),
                                dispatch(fetchAllTaxes()),
                                dispatch(fetchAllWorkflows()),
                                dispatch(fetchAllExpenseAccounts()),
                                dispatch(fetchAllExpenseTags()),
                                dispatch(fetchPerDiemDestinations()),
                                dispatch(fetchAllCsvTemplates()),
                                { ...(featureFlags?.customCsvTemplatesEnabled && dispatch(fetchAllCsvTemplates())) },
                            ]),
                        },
                        dispatch(initialFetchTables()),
                        dispatch(fetchAllEmployees()),
                        dispatch(fetchAllTeams()),
                    ])

                    await dispatch(setHasLoaded())
                    setIsFetching(false)
                    await dispatch(resetFetchFlag())
                } catch (err) {
                    setIsFetching(false)
                    if (err.message === "logout") {
                        dispatch(logout())
                        return
                    }

                    NotificationService.send(NotificationTypeEnum.ERROR, t("error:loader.title"), t("error:loader.message"))
                    // if we encounter an error when fetching, forward to
                    // error page and logout to delete the local storage
                    history.push("/error")
                    dispatch(logout())
                }
            } catch (err) {
                setIsFetching(false)
                NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:error"))
            }
        }
    }

    return <React.Fragment />
})

export default DataFetcher
